import { clearedGroupSuperProperties, toGroupSuperProperties } from './groupSuperProperties';
import { openPanelProfileProperties } from './openpanelProfile';
import type { EventProps, EventShipper, ShipperGroup, ShipperReplay, ShipperUser } from './types';

// Shared surface for web (`@openpanel/web`) and RN (`@openpanel/react-native`).
// Optional methods are web-only today; RN clients omit them and the shipper skips.
export type OpenPanelIdentifyPayload = {
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  properties?: EventProps;
};

export type OpenPanelUpsertGroupPayload = {
  id: string;
  type: string;
  name: string;
  properties?: EventProps;
};

export type OpenPanelClient = {
  track: (name: string, props?: EventProps) => unknown;
  identify: (payload: OpenPanelIdentifyPayload) => unknown;
  screenView: (path: string, props?: EventProps) => void;
  setGlobalProperties: (props: EventProps) => void;
  clear: () => void;
  revenue?: (amount: number, props?: EventProps) => unknown;
  // Native group APIs (OpenPanel 2.3+). Optional so RN stubs can omit them.
  upsertGroup?: (payload: OpenPanelUpsertGroupPayload) => unknown;
  setGroups?: (groupIds: string[]) => unknown;
  // Cookieless visitor id — pass into checkout so backend revenue can attribute.
  getDeviceId?: () => string;
  // Server-assigned session id (returned by `/track`, empty until the first
  // response). Links an error report to this session's replay.
  getSessionId?: () => string;
  groups?: string[];
};

const makeOpenPanelClientStub = (log?: (...args: unknown[]) => void): OpenPanelClient => ({
  track: (name, props) => log?.('openpanel: track', name, props),
  identify: (payload) => log?.('openpanel: identify', payload),
  screenView: (path, props) => log?.('openpanel: screenView', path, props),
  setGlobalProperties: (props) => log?.('openpanel: setGlobalProperties', props),
  clear: () => log?.('openpanel: clear'),
  revenue: (amount, props) => log?.('openpanel: revenue', amount, props),
  upsertGroup: (payload) => log?.('openpanel: upsertGroup', payload),
  setGroups: (groupIds) => log?.('openpanel: setGroups', groupIds),
  getDeviceId: () => {
    log?.('openpanel: getDeviceId');
    return 'stub-device-id';
  },
  getSessionId: () => {
    log?.('openpanel: getSessionId');
    return 'stub-session-id';
  },
  groups: []
});

export const silentOpenPanelClient: OpenPanelClient = makeOpenPanelClientStub();
/* eslint-disable no-console -- console client logs OpenPanel calls instead of sending them */
export const consoleOpenPanelClient: OpenPanelClient = makeOpenPanelClientStub((...args) => console.log(...args));
/* eslint-enable no-console */

// OpenPanel group ids must be unique across types (a single flat id space), so
// namespace the caller's per-type id with its type.
const groupKey = (group: ShipperGroup) => `${group.type}:${group.id}`;

// The web SDK resolves `null` once every retry failed (and on 401); run
// `forget` then so the next sync sends the write again.
const onDeliveryFailure = async (result: unknown, forget: () => void) => {
  try {
    if ((await result) === null) {
      forget();
    }
  } catch {
    forget();
  }
};

// OpenPanel posts one HTTP request per call (no batching), and the SDK retries
// each failed one 3 times. Callers re-sync identity and groups on every effect
// run, so identify / upsertGroup / assign_group skip payloads already sent.
export class OpenPanelClientShipper implements EventShipper {
  readonly name = 'openpanel';
  protected profileId?: string;
  private lastGroupTypes: string[] = [];
  private lastScreenViewPath?: string;
  private lastIdentify?: string;
  // Group ids already assigned to the identified `profileId`.
  private assignedGroups = new Set<string>();
  // Group entities are profile-independent, so this survives `clear()`.
  private groupUpserts = new Map<string, string>();

  constructor(
    protected client: OpenPanelClient,
    private initialized: boolean,
    // Dashboard project page (`https://<host>/<org>/<project>`) the replay link
    // is built on. Without it `getReplay` carries the session id only.
    private readonly projectUrl?: string
  ) {}

  isReady() {
    return this.initialized;
  }

  trackEvent(name: string, props?: EventProps, callback?: () => void) {
    void this.client.track(name, props);
    callback?.();
  }

  measure(name: string, value: number, props?: EventProps) {
    void this.client.track(name, { value, ...props });
  }

  identify(user: ShipperUser) {
    if (user.id !== this.profileId) {
      this.assignedGroups.clear();
    }
    this.profileId = user.id;
    const payload: OpenPanelIdentifyPayload = {
      profileId: user.id,
      email: user.email ?? undefined,
      firstName: user.firstName ?? undefined,
      lastName: user.lastName ?? undefined,
      properties: openPanelProfileProperties(user)
    };
    const signature = JSON.stringify(payload);
    if (signature === this.lastIdentify) {
      return;
    }
    this.lastIdentify = signature;
    void onDeliveryFailure(this.client.identify(payload), () => {
      if (this.lastIdentify === signature) {
        this.lastIdentify = undefined;
      }
    });
  }

  setUserProperty(name: string, value: string | number | boolean) {
    if (!this.profileId) {
      return;
    }
    // The profile no longer matches the last full identify; let the next one through.
    this.lastIdentify = undefined;
    void this.client.identify({ profileId: this.profileId, properties: { [name]: value } });
  }

  register(extra: EventProps) {
    this.client.setGlobalProperties(extra);
  }

  unregister(keys: string[]) {
    this.client.setGlobalProperties(Object.fromEntries(keys.map((key) => [key, ''])));
  }

  setGroups(groups: ShipperGroup[]) {
    // Namespace ids (`type:id`) — OpenPanel membership is a flat id space, and
    // org/user_group can share the same numeric id.
    const keyed = groups.map((group) => ({ group, id: groupKey(group) }));
    const keys = keyed.map(({ id }) => id);
    keyed.forEach(({ group, id }) => {
      const payload: OpenPanelUpsertGroupPayload = {
        id,
        type: group.type,
        name: group.name ?? group.id,
        properties: group.properties
      };
      const signature = JSON.stringify(payload);
      if (this.groupUpserts.get(id) === signature) {
        return;
      }
      this.groupUpserts.set(id, signature);
      void onDeliveryFailure(this.client.upsertGroup?.(payload), () => {
        if (this.groupUpserts.get(id) === signature) {
          this.groupUpserts.delete(id);
        }
      });
    });
    // The SDK's setGroups unions into `groups`, so a deselected group would stay
    // on every later event — write the field first to make it a replace.
    if ('groups' in this.client) {
      this.client.groups = keys;
    }
    // assign_group unions into the profile server-side, so an identified
    // profile only needs ids it does not have yet. Anonymous assignments go to a
    // server-derived device profile (IP, user agent, rotating salt) that can
    // change under us, so they always send.
    const { profileId } = this;
    const unassigned = profileId ? keys.filter((key) => !this.assignedGroups.has(key)) : keys;
    if (unassigned.length > 0) {
      if (profileId) {
        unassigned.forEach((key) => this.assignedGroups.add(key));
      }
      void onDeliveryFailure(this.client.setGroups?.(unassigned), () => {
        if (this.profileId === profileId) {
          unassigned.forEach((key) => this.assignedGroups.delete(key));
        }
      });
    }
    // Super-props keep group labels filterable on events (alongside native groups).
    const removedTypes = this.lastGroupTypes.filter((type) => !groups.some((group) => group.type === type));
    this.lastGroupTypes = [...new Set(groups.map((group) => group.type))];
    this.client.setGlobalProperties({
      ...toGroupSuperProperties(groups),
      ...clearedGroupSuperProperties(removedTypes)
    });
  }

  clear() {
    this.profileId = undefined;
    this.lastGroupTypes = [];
    this.lastScreenViewPath = undefined;
    this.lastIdentify = undefined;
    this.assignedGroups.clear();
    // Local reset only: an empty assign_group is a no-op POST (profile groups
    // are a server-side union).
    if ('groups' in this.client) {
      this.client.groups = [];
    }
    this.client.clear();
  }

  // OpenPanel posts one HTTP request per event (no batching) — identical paths
  // must not leave the shipper. Filter/pagination query churn and StrictMode
  // remounts would otherwise POST a new /track.
  screenView(path: string, props?: EventProps) {
    if (path === this.lastScreenViewPath) {
      return;
    }
    this.lastScreenViewPath = path;
    this.client.screenView(path, props);
  }

  revenue(amount: number, props?: EventProps) {
    void this.client.revenue?.(amount, props);
  }

  getDeviceId(): string | undefined {
    return this.client.getDeviceId?.();
  }

  // The session id is assigned by the OpenPanel server on the first `/track`
  // response, so it is exactly the id the dashboard shows — but empty until
  // then and after `clear()`.
  getReplay(): ShipperReplay | undefined {
    const sessionId = this.client.getSessionId?.();
    if (!sessionId) {
      return undefined;
    }
    const base = this.projectUrl?.replace(/\/+$/, '');
    return { sessionId, url: base ? `${base}/sessions/${encodeURIComponent(sessionId)}` : undefined };
  }
}
